import * as React from "react"
import type {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  Table as TableInstance,
  TableOptions,
  VisibilityState,
} from "@tanstack/react-table"
import { EmptyState } from "@/components/ui/EmptyState"
import {
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import { Input } from "@/components/ui/shadcn/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/shadcn/table"
import { cn } from "@/lib/utils"

import { DataTablePagination } from "./DataTablePagination"

type ToolbarRenderer<TData> = (table: TableInstance<TData>) => React.ReactNode

type CustomTableOptions<TData> = Partial<
  Omit<
    TableOptions<TData>,
    | "data"
    | "columns"
    | "state"
    | "onSortingChange"
    | "onColumnFiltersChange"
    | "onColumnVisibilityChange"
    | "onRowSelectionChange"
    | "getCoreRowModel"
    | "getPaginationRowModel"
    | "getSortedRowModel"
    | "getFilteredRowModel"
    | "getFacetedRowModel"
    | "getFacetedUniqueValues"
    | "getFacetedMinMaxValues"
  >
>

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  className?: string
  search?: {
    columnId: string
    placeholder?: string
  }
  renderToolbarContent?: ToolbarRenderer<TData>
  renderToolbarActions?: ToolbarRenderer<TData>
  emptyState?: React.ReactNode
  EmptyStateComponent?: React.ComponentType<{ title: string; description?: string; action?: React.ReactNode }>
  enableColumnVisibilityToggle?: boolean
  enableRowSelection?: boolean
  initialColumnVisibility?: VisibilityState
  initialSorting?: SortingState
  initialColumnFilters?: ColumnFiltersState
  onSortingChange?: (sorting: SortingState) => void
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void
  onColumnVisibilityChange?: (visibility: VisibilityState) => void
  pageSize?: number
  pageSizeOptions?: number[]
  mobileRowRenderer?: (row: TData) => React.ReactNode
  getRowId?: (row: TData, index: number, parent?: unknown) => string
  showPagination?: boolean
  isLoading?: boolean
  loadingState?: React.ReactNode
  tableOptions?: CustomTableOptions<TData>
}

export function DataTable<TData, TValue>({
  columns,
  data,
  className,
  search,
  renderToolbarContent,
  renderToolbarActions,
  emptyState,
  enableRowSelection = false,
  initialColumnVisibility,
  initialSorting,
  initialColumnFilters,
  onSortingChange,
  onColumnFiltersChange,
  onColumnVisibilityChange,
  pageSize = 20,
  pageSizeOptions,
  mobileRowRenderer,
  getRowId,
  showPagination = true,
  isLoading = false,
  loadingState,
  tableOptions,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>(
    () => initialSorting ?? [],
  )
  const [columnFilters, setColumnFilters] =
    React.useState<ColumnFiltersState>(() => initialColumnFilters ?? [])
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>(() => initialColumnVisibility ?? {})
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})

  React.useEffect(() => {
    if (initialColumnVisibility) {
      setColumnVisibility(initialColumnVisibility)
    }
  }, [initialColumnVisibility])

  React.useEffect(() => {
    if (initialSorting) {
      setSorting(initialSorting)
    }
  }, [initialSorting])

  React.useEffect(() => {
    if (initialColumnFilters) {
      setColumnFilters(initialColumnFilters)
    }
  }, [initialColumnFilters])

  const handleSortingChange = React.useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      setSorting((previous) => {
        const next =
          typeof updater === "function" ? updater(previous) : updater
        onSortingChange?.(next as SortingState)
        return next as SortingState
      })
    },
    [onSortingChange],
  )

  const handleColumnFiltersChange =
    React.useCallback<OnChangeFn<ColumnFiltersState>>(
      (updater) => {
        setColumnFilters((previous) => {
          const next =
            typeof updater === "function" ? updater(previous) : updater
          onColumnFiltersChange?.(next as ColumnFiltersState)
          return next as ColumnFiltersState
        })
      },
      [onColumnFiltersChange],
    )

  const handleColumnVisibilityChange =
    React.useCallback<OnChangeFn<VisibilityState>>(
      (updater) => {
        setColumnVisibility((previous) => {
          const next =
            typeof updater === "function" ? updater(previous) : updater
          onColumnVisibilityChange?.(next as VisibilityState)
          return next as VisibilityState
        })
      },
      [onColumnVisibilityChange],
    )

  const defaultLoadingState = React.useMemo(
    () => (
      <div className="flex items-center justify-center gap-2">
        <div className="size-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    ),
    [],
  )

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    enableRowSelection,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onRowSelectionChange: enableRowSelection ? setRowSelection : undefined,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    initialState: {
      pagination: {
        pageSize,
      },
    },
    ...(getRowId ? { getRowId } : {}),
    ...(tableOptions ?? {}),
  })

  const searchColumn = search?.columnId
    ? table.getColumn(search.columnId)
    : null
  const toolbarContent = renderToolbarContent?.(table)
  const toolbarActions = renderToolbarActions?.(table)
  const resolvedEmptyState = React.useMemo(() => {
    if (typeof emptyState === 'string') {
      return <EmptyState title={emptyState} />;
    }
    if (React.isValidElement(emptyState)) {
      return emptyState;
    }
    return emptyState ?? <EmptyState title="No results found." />;
  }, [emptyState]);
  const resolvedLoadingState = loadingState ?? defaultLoadingState

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {searchColumn ? (
            <Input
              placeholder={search?.placeholder ?? "Search..."}
              value={(searchColumn.getFilterValue() as string) ?? ""}
              onChange={(event) =>
                searchColumn.setFilterValue(event.target.value)
              }
              className="h-9 w-full max-w-xs sm:max-w-[18rem]"
            />
          ) : null}
          {toolbarContent}
        </div>
        <div className="flex items-center gap-2">
          {toolbarActions}
          {/* {enableColumnVisibilityToggle ? (
            <DataTableViewOptions table={table} />
          ) : null} */}
        </div>
      </div>

      <div
        className={cn(
          "rounded-md border bg-background",
          mobileRowRenderer ? "hidden lg:block" : "block",
        )}
      >
        <div className="w-full overflow-x-auto">
          <Table className="min-w-full text-sm">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-sm"
                  >
                    {resolvedLoadingState}
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-sm"
                  >
                    {resolvedEmptyState}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {mobileRowRenderer ? (
        <div className="space-y-3 lg:hidden" style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}>
          {isLoading ? (
            <div className="rounded-md border border-dashed border-muted-foreground/40 p-6 text-center text-sm text-muted-foreground">
              {resolvedLoadingState}
            </div>
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                {mobileRowRenderer(row.original)}
              </React.Fragment>
            ))
          ) : (
            <div className="rounded-md border border-dashed border-muted-foreground/40 p-6 text-center text-sm text-muted-foreground">
              {resolvedEmptyState}
            </div>
          )}
        </div>
      ) : null}

      {showPagination ? (
        <DataTablePagination
          table={table}
          pageSizeOptions={pageSizeOptions}
          showSelectedCount={enableRowSelection}
        />
      ) : null}
    </div>
  )
}
